import { ThunderDispatcherV3 } from '@nu-art/thunderstorm-frontend/core/db-api-gen/types';
import { BuilderExpressionTypes, DBDef_Expression, Type_ClipboardAtom, Type_ClipboardTerm, Validator_ExpressionMinimalAssertion } from '@app/hcs-shared';
import { ModuleFE_QuaiProtoItem } from '@app/styles/frontend/modules/ModuleFE_QuaiProtoItem/ModuleFE_QuaiProtoItem';
import { PermissionKey_FE } from '@nu-art/permissions-frontend/PermissionKey_FE';
import { __stringify, exists, isValidJson, MimeType_txt, Second } from '@nu-art/ts-common';
import { ModuleFE_Toaster } from '@nu-art/thunderstorm-frontend/index';
import { ExpressionEditorPermissionKeys, ExpressionPermissionKeys } from '@app/hcs-shared/expression/permissions';
import { editableTerm_isWithSubTerms, rightHand_isRange, rightHand_isValue, term_isFlat } from './utils.js';
export const dispatch_onExpressionChanged = new ThunderDispatcherV3('__onExpressionUpdated');
export class ModuleFE_Expression_Class extends ModuleFE_QuaiProtoItem {
    _v1;
    permissions;
    __searchFilterMapper = (item) => [item.label ?? item.description ?? '', item._id];
    __searchGroups = (items) => ([{
            group: this.dbDef.entityName,
            results: items.filter(i => BuilderExpressionTypes.map(e => e.dbKey).includes(i.expressionType)).map(i => {
                return { _id: i._id, uiLabel: i.label };
            })
        }]);
    constructor() {
        super(DBDef_Expression, dispatch_onExpressionChanged, Validator_ExpressionMinimalAssertion);
        this._v1 = {};
        // permission declaration
        this.permissions = {
            ui: PermissionKey_FE.generatePermissionKeysByLevels(ExpressionEditorPermissionKeys),
            collection: PermissionKey_FE.generatePermissionKeysByLevels(ExpressionPermissionKeys)
        };
    }
    /**
     * Copy any {type, data} payload to the clipboard as text, then notify listeners so paste buttons refresh.
     * A dedicated 'expression-clipboard-changed' event is dispatched because programmatic clipboard writes don't fire the native 'copy' one.
     */
    copyToClipboard = async (type, data, label) => {
        try {
            const blob = new Blob([__stringify({ type, data })], { type: MimeType_txt });
            await navigator.clipboard.write([new ClipboardItem({
                    [blob.type]: blob
                })]);
            window.dispatchEvent(new Event('expression-clipboard-changed'));
            return ModuleFE_Toaster.toastSuccess(`${label} copied to clipboard.`, 3 * Second);
        }
        catch (err) {
            this.logError(err);
            return ModuleFE_Toaster.toastError(`Could not copy ${label}`, 3 * Second);
        }
    };
    /**
     * Read the first clipboard payload matching the given type (and optional shape validator).
     * Web clipboard api can only store a single item at the time, so the first match is returned.
     */
    getFromClipboard = async (type, validator) => {
        try {
            const clipboardItems = await navigator.clipboard.read();
            for (const item of clipboardItems) {
                if (item.types.includes(MimeType_txt)) {
                    const found = await this.resolveFromClipboard(item, type, validator);
                    if (found)
                        return found;
                }
            }
        }
        catch (err) {
            this.logError(err);
        }
    };
    /**
     * Resolve and parse a typed payload out of the clipboard string, validating both the type and (optionally) the shape
     */
    resolveFromClipboard = async (clipboardItem, type, validator) => {
        const itemContent = await (await clipboardItem.getType(MimeType_txt)).text();
        if (!isValidJson(itemContent))
            return;
        const parsedItem = JSON.parse(itemContent);
        if (parsedItem.type !== type)
            return;
        if (validator && !validator(parsedItem.data))
            return;
        return parsedItem;
    };
    copyTermToClipboard = async (term, index) => {
        return this.copyToClipboard(Type_ClipboardTerm, term, `Term (T${index + 1})`);
    };
    getCopiedTerm = async () => {
        return this.getFromClipboard(Type_ClipboardTerm);
    };
    copyAtomToClipboard = async (atom, index) => {
        return this.copyToClipboard(Type_ClipboardAtom, atom, `Atom (A${index + 1})`);
    };
    getCopiedAtom = async () => {
        return this.getFromClipboard(Type_ClipboardAtom, this.isValidAtom);
    };
    isValidAtom = (data) => {
        return exists(data) && typeof data === 'object' && Array.isArray(data.cases);
    };
    isRightHand = { range: rightHand_isRange, values: rightHand_isValue };
    isTerm = { flat: term_isFlat, withSubTerms: editableTerm_isWithSubTerms };
}
export const ModuleFE_Expression = new ModuleFE_Expression_Class();
//# sourceMappingURL=ModuleFE_Expression.js.map